/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-03-22 09:33:26
 **/

import { StateCreator, StoreMutatorIdentifier } from "zustand";
import { nanoid } from "nanoid";
import omit from "lodash-es/omit";
import { Properties } from "@/properties";

export type SyncOptions<T = unknown> = {
	channel_name?: string;
	use_debug?: boolean;
	exclude_keys?: string[];
	initialState?: T;
};

export type Sync = <
	T extends object,
	Mps extends [StoreMutatorIdentifier, unknown][] = [],
	Mcs extends [StoreMutatorIdentifier, unknown][] = []
>(
	f: StateCreator<T, Mps, Mcs>,
	options?: SyncOptions<T>
) => StateCreator<T, Mps, Mcs>;
type SyncImpl = <T>(f: StateCreator<T, [], []>, options?: SyncOptions<T>) => StateCreator<T, [], []>;

const LOGTAG = "#storage/plugin/sync#";

const sync_impl: SyncImpl = (f, options) => (set, get, store) => {
	if (!globalThis.BroadcastChannel) {
		if (options?.use_debug) {
			console.debug(LOGTAG, "BroadcastChannel is not supported");
		}
		return f(set, get, store);
	}

	let is_synced = false;
	const id = nanoid(64);
	const channel = new BroadcastChannel(options?.channel_name ?? "zustand-shared");

	if (options?.use_debug) {
		console.debug(LOGTAG, "channel_name", options?.channel_name ?? "zustand-shared");
		console.debug(LOGTAG, "id", id);
	}

	channel.addEventListener("message", (e) => {
		const { id: from_id, tab_id, state } = e.data;
		if (options?.use_debug) {
			console.group(LOGTAG, "message");
			console.debug(LOGTAG, "from_id:", from_id);
			console.debug(LOGTAG, "to_id:", id);
			console.debug(LOGTAG, "tab_id:", tab_id);
			console.debug(LOGTAG, "state:", state);
			console.groupEnd();
		}
		if (from_id !== id && tab_id === Properties.instance.browser_tab_id) {
			is_synced = true;
			set(state);
		}
	});

	store.subscribe((state) => {
		if (is_synced) {
			is_synced = false;
			return;
		}

		channel.postMessage({
			id,
			tab_id: Properties.instance.browser_tab_id,
			state: omit(state as object, options?.exclude_keys ?? [])
		});
	});

	return f(set, get, store);
};

export const sync = sync_impl as Sync;
